import { PLAN_PERMISSIONS_SECTIONS } from "../../redux/modules/auth/userPermissions/selector";
import { SID_NOTIFICATION_TYPES } from "../notifications/sidNotificationTypes";
import { SUPPLY_DEMAND_NOTIFICATION_TYPES } from "../notifications/supplyDemandNotificationTypes";
import { TREND_NOTIFICATION_TYPES } from "../notifications/trendNotificationTypes";
import { STRATEGY_BENCHMARK_CHART_STRATEGY_IDS } from "../StrategyBenchmarkChart/strategyBenchmarkChartConfig";
import ResourceResolver from "../UniversalTable/ResourceResolver";

export const STRATEGY_DASHBOARD_IDS = Object.freeze({
  HIGHS_AND_LOWS: "highs_and_lows",
  SUPPLY_DEMAND: "supply_demand",
  TREND: "trend"
});

export const STRATEGY_DASHBOARD_TABS = Object.freeze({
  DESCRIPTION: "description",
  TABLE: "table",
  SIGNALS: "signals"
});

const STRATEGY_DASHBOARD_TAB_URL_PATTERNS = Object.freeze({
  [STRATEGY_DASHBOARD_TABS.DESCRIPTION]: Object.freeze({
    en: "description",
    pl: "opis"
  }),
  [STRATEGY_DASHBOARD_TABS.TABLE]: Object.freeze({
    en: "table",
    pl: "tabela"
  }),
  [STRATEGY_DASHBOARD_TABS.SIGNALS]: Object.freeze({
    en: "signals",
    pl: "sygnaly"
  })
});

function createUrlPatternKey(component, tab) {
  if (!tab) {
    return Object.freeze({ component });
  }

  return Object.freeze({
    component,
    urlPatterns: STRATEGY_DASHBOARD_TAB_URL_PATTERNS[tab]
  });
}

/** Empty-state translation key suffixes (prefixed with dashboard `i18nPrefix`). */
const NOTIFICATION_EMPTY_STATE_TRANSLATION_KEYS = Object.freeze({
  enableTitle: "enable_notifications_title",
  enableDescription: "enable_notifications_description",
  enableButton: "enable_notifications_button",
  noYetTitle: "no_notifications_yet_title",
  noYetDescription: "no_notifications_yet_description"
});

export const STRATEGY_DASHBOARD_EMPTY_STATE_TRANSLATION_KEY = Object.freeze({
  ENABLE_TITLE: "enableTitle",
  ENABLE_DESCRIPTION: "enableDescription",
  ENABLE_BUTTON: "enableButton",
  NO_YET_TITLE: "noYetTitle",
  NO_YET_DESCRIPTION: "noYetDescription"
});

const STRATEGY_DASHBOARD_CONFIG = Object.freeze({
  [STRATEGY_DASHBOARD_IDS.HIGHS_AND_LOWS]: Object.freeze({
    id: STRATEGY_DASHBOARD_IDS.HIGHS_AND_LOWS,
    i18nPrefix: "sid_dashboard",
    planPermissionSection: PLAN_PERMISSIONS_SECTIONS.HIGHS_AND_LOWS,
    notificationsPermissionSection: PLAN_PERMISSIONS_SECTIONS.SID_NOTIFICATIONS,
    descriptionSlug: "sid-dashboard-description-page",
    chartStrategyId: STRATEGY_BENCHMARK_CHART_STRATEGY_IDS.HIGHS_AND_LOWS,
    notificationTypes: SID_NOTIFICATION_TYPES,
    ebook: true,
    indicatorsTable: Object.freeze({
      resourceId: ResourceResolver.highs_and_lows_table
    }),
    signalsTable: Object.freeze({
      resourceId: ResourceResolver.sid_signals_table
    }),
    web: Object.freeze({
      paths: Object.freeze({
        en: "strategy/swing-and-direction",
        pl: "strategie/szczyty-i-dna"
      }),
      urlPatternKeys: Object.freeze({
        overview: createUrlPatternKey("sidDashboard"),
        description: createUrlPatternKey(
          "sidDashboardDescriptionTab",
          STRATEGY_DASHBOARD_TABS.DESCRIPTION
        ),
        table: createUrlPatternKey(
          "sidDashboardTableTab",
          STRATEGY_DASHBOARD_TABS.TABLE
        ),
        signals: createUrlPatternKey(
          "sidDashboardSignalsTab",
          STRATEGY_DASHBOARD_TABS.SIGNALS
        )
      })
    }),
    native: Object.freeze({
      scenes: Object.freeze({
        overview: "sidDashboard",
        description: "sidDashboardDescription",
        table: "sidDashboardTable",
        signals: "sidDashboardSignals"
      }),
      routes: Object.freeze({
        overview: "/(tabs)/menu/sid-dashboard",
        description: "/(tabs)/menu/sid-dashboard/description",
        table: "/(tabs)/menu/sid-dashboard/table",
        signals: "/(tabs)/menu/sid-dashboard/signals"
      })
    })
  }),
  [STRATEGY_DASHBOARD_IDS.SUPPLY_DEMAND]: Object.freeze({
    id: STRATEGY_DASHBOARD_IDS.SUPPLY_DEMAND,
    i18nPrefix: "supply_demand_dashboard",
    planPermissionSection: PLAN_PERMISSIONS_SECTIONS.SUPPLY_DEMAND,
    descriptionSlug: "supply-demand-dashboard-description-page",
    chartStrategyId: STRATEGY_BENCHMARK_CHART_STRATEGY_IDS.SUPPLY_DEMAND,
    notificationTypes: SUPPLY_DEMAND_NOTIFICATION_TYPES,
    ebook: true,
    ...(ResourceResolver.supply_and_demand_alerts_table
      ? {
          indicatorsTable: Object.freeze({
            resourceId: ResourceResolver.supply_and_demand_alerts_table,
            fitColumns: true
          })
        }
      : {}),
    ...(ResourceResolver.supply_and_demand_table
      ? {
          signalsTable: Object.freeze({
            resourceId: ResourceResolver.supply_and_demand_table,
            fitColumns: true,
            stackZoneEdges: true
          })
        }
      : {}),
    web: Object.freeze({
      paths: Object.freeze({
        en: "strategy/supply-and-demand-zones",
        pl: "strategie/strefy-popytu"
      }),
      urlPatternKeys: Object.freeze({
        overview: createUrlPatternKey("supplyDemandDashboard"),
        description: createUrlPatternKey(
          "supplyDemandDashboardDescriptionTab",
          STRATEGY_DASHBOARD_TABS.DESCRIPTION
        ),
        table: createUrlPatternKey(
          "supplyDemandDashboardTableTab",
          STRATEGY_DASHBOARD_TABS.TABLE
        ),
        signals: createUrlPatternKey(
          "supplyDemandDashboardSignalsTab",
          STRATEGY_DASHBOARD_TABS.SIGNALS
        )
      })
    }),
    native: Object.freeze({
      scenes: Object.freeze({
        overview: "supplyDemandDashboard",
        description: "supplyDemandDashboardDescription",
        table: "supplyDemandDashboardTable",
        signals: "supplyDemandDashboardSignals"
      }),
      routes: Object.freeze({
        overview: "/(tabs)/menu/supply-demand-dashboard",
        description: "/(tabs)/menu/supply-demand-dashboard/description",
        table: "/(tabs)/menu/supply-demand-dashboard/table",
        signals: "/(tabs)/menu/supply-demand-dashboard/signals"
      })
    })
  }),
  [STRATEGY_DASHBOARD_IDS.TREND]: Object.freeze({
    id: STRATEGY_DASHBOARD_IDS.TREND,
    i18nPrefix: "trend_dashboard",
    planPermissionSection: PLAN_PERMISSIONS_SECTIONS.TREND_STRATEGY,
    descriptionSlug: "trend-dashboard-description-page",
    chartStrategyId: STRATEGY_BENCHMARK_CHART_STRATEGY_IDS.TREND,
    chartMarkets: Object.freeze(["GPW", "NASDAQ", "NYSE"]),
    notificationTypes: TREND_NOTIFICATION_TYPES,
    ...(ResourceResolver.trend_strategy_signals_table
      ? {
          signalsTable: Object.freeze({
            resourceId: ResourceResolver.trend_strategy_signals_table
          })
        }
      : {}),
    web: Object.freeze({
      paths: Object.freeze({
        en: "strategy/trend",
        pl: "strategie/trendowa"
      }),
      urlPatternKeys: Object.freeze({
        overview: createUrlPatternKey("trendDashboard"),
        description: createUrlPatternKey(
          "trendDashboardDescriptionTab",
          STRATEGY_DASHBOARD_TABS.DESCRIPTION
        ),
        signals: createUrlPatternKey(
          "trendDashboardSignalsTab",
          STRATEGY_DASHBOARD_TABS.SIGNALS
        )
      })
    }),
    native: Object.freeze({
      scenes: Object.freeze({
        overview: "trendDashboard",
        description: "trendDashboardDescription",
        signals: "trendDashboardSignals"
      }),
      routes: Object.freeze({
        overview: "/(tabs)/menu/trend-dashboard",
        description: "/(tabs)/menu/trend-dashboard/description",
        signals: "/(tabs)/menu/trend-dashboard/signals"
      })
    })
  })
});

export function getStrategyDashboardConfig(strategyId) {
  const dashboard = STRATEGY_DASHBOARD_CONFIG[strategyId];

  if (!dashboard) {
    throw new Error(
      `Unknown strategy dashboard "${strategyId}". Valid values: ${Object.values(
        STRATEGY_DASHBOARD_IDS
      ).join(", ")}`
    );
  }

  return dashboard;
}

export function getStrategyDashboardWebPathLocale(dashboard, pathStem) {
  return (
    Object.keys(dashboard.web.paths).find(
      locale => dashboard.web.paths[locale] === pathStem
    ) || null
  );
}

export function resolveDashboardTab(dashboard, tabParam, locale) {
  if (!tabParam || !locale) {
    return null;
  }

  return (
    Object.values(STRATEGY_DASHBOARD_TABS).find(tab => {
      const entry = dashboard.web.urlPatternKeys[tab];
      return entry?.urlPatterns?.[locale] === tabParam;
    }) || null
  );
}

export function getStrategyDashboardI18nKey(dashboard, suffix) {
  return `${dashboard.i18nPrefix}_${suffix}`;
}

export function getStrategyDashboardEmptyStateTranslationKey(dashboard, keyName) {
  const suffix = NOTIFICATION_EMPTY_STATE_TRANSLATION_KEYS[keyName];

  if (!suffix) {
    throw new Error(
      `Unknown notification empty-state translation key "${keyName}"`
    );
  }

  return getStrategyDashboardI18nKey(dashboard, suffix);
}

export function getStrategyDashboardPageTitleKey(dashboard, tab) {
  if (!tab) {
    return getStrategyDashboardI18nKey(dashboard, "page_title");
  }

  return getStrategyDashboardI18nKey(dashboard, `${tab}_page_title`);
}

export function getStrategyDashboardByWebPath(pathStem) {
  return Object.values(STRATEGY_DASHBOARD_CONFIG).find(dashboard =>
    Object.values(dashboard.web.paths).includes(pathStem)
  );
}

export function getStrategyDashboards() {
  return Object.values(STRATEGY_DASHBOARD_CONFIG);
}

export function shouldShowStrategyDashboardSignals(dashboard) {
  return Boolean(dashboard.signalsTable);
}

/**
 * Exact locale-paired routes (path stem + matching tab slug).
 * Avoids mixed-language URLs like `/strategie/szczyty-i-dna/description`.
 */
export function getStrategyDashboardWebRoutes(dashboard) {
  return Object.entries(dashboard.web.paths).flatMap(([locale, pathStem]) => {
    const routes = [{ path: `/${pathStem}`, tab: null }];

    Object.values(STRATEGY_DASHBOARD_TABS).forEach(tab => {
      const segment = dashboard.web.urlPatternKeys[tab]?.urlPatterns?.[locale];
      if (!segment) {
        return;
      }

      routes.push({
        path: `/${pathStem}/${segment}`,
        tab
      });
    });

    return routes;
  });
}
